import { menuListItems } from "assets/menu/menuList";
import { Button } from "components/Button";
import { Loading } from "components/Loading";
import { Menu } from "components/Menu";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatDateIso, formatDateTime, orEmpty } from "../formatters";
import { Actions, Container, JsonBlock, Section, Summary } from "./styles";

interface TicketIngestionBatchPayload {
  ticketIngestionBatchId: string;
  batchId?: string | null;
  eventSlug: string;
  source: string;
  received: number;
  remoteIp?: string | null;
  userAgent?: string | null;
  body: unknown;
  insertDate: string;
}

const QUERY = `
  query BackofficeTicketIngestionBatchPayload($ticketIngestionBatchId: ID!) {
    backofficeTicketIngestionBatchPayload(
      ticketIngestionBatchId: $ticketIngestionBatchId
    ) {
      ticketIngestionBatchId
      batchId
      eventSlug
      source
      received
      remoteIp
      userAgent
      body
      insertDate
    }
  }
`;

/**
 * Corpo da requisição do parceiro como chegou, aberto em outra aba a partir
 * da tabela de lotes. Contém dado pessoal (nome, CPF, e-mail, telefone).
 */
export const TicketIngestionBatchPayload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState<TicketIngestionBatchPayload | null>(
    null
  );

  useEffect(() => {
    connectGraphQLApi({
      query: QUERY,
      variables: { ticketIngestionBatchId: id },
      errorMessage: "Falha ao carregar o payload do lote",
      useNewApi: true,
    })
      .then((response: any) => {
        setPayload(response.data.backofficeTicketIngestionBatchPayload);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const json = useMemo(
    () => (payload ? JSON.stringify(payload.body, null, 2) : ""),
    [payload]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setToast({
        type: "success",
        message: "JSON copiado.",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } catch {
      setToast({
        type: "error",
        message: "Não foi possível copiar o JSON.",
        isActive: true,
        timeToRemoveToast: 4000,
      });
    }
  };

  const handleDownload = () => {
    if (!payload) return;
    const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = [
      "payload",
      payload.eventSlug,
      payload.batchId || payload.ticketIngestionBatchId,
      formatDateIso(new Date(payload.insertDate)),
    ]
      .join("-")
      .concat(".json");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Menu links={menuListItems}>
      {loading ? (
        <Loading />
      ) : error ? (
        <PlaceHolderNotExists title="Erro ao se comunicar com o servidor" />
      ) : !payload ? (
        <PlaceHolderNotExists title="Payload não encontrado para este lote" />
      ) : (
        <Container>
          <Section>
            <h2 style={{ marginBottom: "1.5rem" }}>Payload do lote</h2>
            <Summary>
              <div>
                <dt>Recebido em</dt>
                <dd>{formatDateTime(payload.insertDate)}</dd>
              </div>
              <div>
                <dt>Origem</dt>
                <dd>{payload.source}</dd>
              </div>
              <div>
                <dt>Evento</dt>
                <dd>{payload.eventSlug}</dd>
              </div>
              <div>
                <dt>Lote</dt>
                <dd>{orEmpty(payload.batchId)}</dd>
              </div>
              <div>
                <dt>Itens recebidos</dt>
                <dd>{payload.received}</dd>
              </div>
              <div>
                <dt>IP de origem</dt>
                <dd>{orEmpty(payload.remoteIp)}</dd>
              </div>
            </Summary>
          </Section>
          <Section>
            <Actions>
              <Button
                label="Copiar JSON"
                typeBtn="blue"
                type="button"
                onClick={handleCopy}
                block={false}
              />
              <Button
                label="Baixar JSON"
                typeBtn="outline"
                type="button"
                onClick={handleDownload}
                block={false}
              />
            </Actions>
            <JsonBlock>{json}</JsonBlock>
          </Section>
        </Container>
      )}
    </Menu>
  );
};
